import React, { useEffect, useState } from 'react';
import { useClientPortal } from '../../context/ClientPortalContext';
import { PortalCredentialsService } from '../../services/portal/portalCredentialsService';
import { KeyIcon, EyeIcon, EyeSlashIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';

const ClientCredentialsPage = () => {
    const { clientId } = useClientPortal();
    const [credentials, setCredentials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visiblePasswords, setVisiblePasswords] = useState({});

    useEffect(() => {
        if (clientId) {
            loadCredentials();
        }
    }, [clientId]);

    const loadCredentials = async () => {
        setLoading(true);
        const { data } = await PortalCredentialsService.listCredentials(clientId);
        setCredentials(data || []);
        setLoading(false);
    };

    const togglePasswordVisibility = (id) => {
        setVisiblePasswords(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // Could add a toast notification here
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-slate-900">My Credentials</h1>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {loading ? (
                    <div className="p-6 text-center text-slate-500">Loading credentials...</div>
                ) : credentials.length === 0 ? (
                    <div className="p-12 text-center text-slate-500">
                        <KeyIcon className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                        <p>No credentials shared with you yet.</p>
                    </div>
                ) : (
                    <div className="grid gap-6 p-6 sm:grid-cols-2 lg:grid-cols-3">
                        {credentials.map((cred) => (
                            <div key={cred.id} className="border border-slate-200 rounded-xl p-5 bg-slate-50 hover:shadow-md transition-shadow">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                                        <KeyIcon className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-900">{cred.label || 'Service'}</h3>
                                        <a href={cred.url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline truncate max-w-[150px] block">
                                            {cred.url}
                                        </a>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div>
                                        <label className="text-xs font-medium text-slate-500 uppercase">Username</label>
                                        <div className="flex items-center justify-between bg-white border border-slate-200 rounded px-3 py-2 mt-1">
                                            <span className="text-sm text-slate-800 truncate select-all">{cred.username}</span>
                                            <button onClick={() => copyToClipboard(cred.username)} className="text-slate-400 hover:text-indigo-600">
                                                <ClipboardDocumentIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-xs font-medium text-slate-500 uppercase">Password</label>
                                        <div className="flex items-center justify-between bg-white border border-slate-200 rounded px-3 py-2 mt-1">
                                            <span className="text-sm text-slate-800 truncate font-mono">
                                                {visiblePasswords[cred.id] ? cred.password_encrypted : '••••••••'}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => togglePasswordVisibility(cred.id)} className="text-slate-400 hover:text-indigo-600">
                                                    {visiblePasswords[cred.id] ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                                                </button>
                                                <button onClick={() => copyToClipboard(cred.password_encrypted)} className="text-slate-400 hover:text-indigo-600">
                                                    <ClipboardDocumentIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientCredentialsPage;
